import * as React from "react";
import { CheckCircle2, X, XCircle } from "lucide-react";

import { cn } from "@/lib/utils";

export type ToastTone = "success" | "failure" | "info";

export type Toast = {
  id: string;
  tone: ToastTone;
  title: string;
  body?: string;
};

/**
 * Stacks toasts in the top-right corner. Each entry auto-dismisses after
 * {@link AUTO_DISMISS_MS}; the user can also close manually. Used by the
 * ChatPage to surface negotiation outcomes (success / failure) with more
 * weight than the in-chat bubble alone.
 */
export function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div className="pointer-events-none fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col gap-2">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

const AUTO_DISMISS_MS = 6000;

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  React.useEffect(() => {
    const t = window.setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [onDismiss]);

  const Icon = toast.tone === "success" ? CheckCircle2 : toast.tone === "failure" ? XCircle : null;
  const bg =
    toast.tone === "success"
      ? "bg-brand-teal/15"
      : toast.tone === "failure"
        ? "bg-destructive/15"
        : "bg-brand-blue/15";
  const accent =
    toast.tone === "success"
      ? "text-brand-teal"
      : toast.tone === "failure"
        ? "text-destructive"
        : "text-brand-blue";

  return (
    <div
      role="status"
      className={cn(
        "comic-shadow pointer-events-auto flex animate-toast-in items-start gap-2 rounded-md border border-foreground p-3 font-comic-body text-foreground",
        bg,
      )}
    >
      {Icon && <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", accent)} />}
      <div className="flex-1 text-sm">
        <div className="font-comic-headline uppercase tracking-wide">{toast.title}</div>
        {toast.body && <div className="mt-0.5 text-xs">{toast.body}</div>}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="rounded p-0.5 transition hover:bg-foreground/10"
        aria-label="dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/**
 * Tiny toast queue hook. Keeps the API surface small: {@code push} adds an
 * entry, {@code dismiss} removes one. Auto-dismissal lives in ToastCard.
 */
export function useToasts() {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const push = React.useCallback((t: Omit<Toast, "id">) => {
    setToasts((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ...t },
    ]);
  }, []);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, push, dismiss };
}
